import axios from "axios";
import type { api as apiTypes } from "roo-server";
import { DoFetch } from "create-typed-sdk";
import { createTypedReactSDK } from "create-typed-react-sdk";
import { sharedUniversal } from "roo-shared";
import { getApiRootUrl } from "../env.js";

/**
 * The typed SDK over the USER API (`api`), the same factory the mobile app and the admin SPA use.
 * Everything the server exposes is reachable and fully typed from here — the whole `api` namespace
 * comes across for free; only `doFetch` is per-platform.
 *
 * Differences from the admin SPA's copy, both deliberate:
 *   - `api` not `apiAdmin`, and therefore `/bifrost/` not `/bifrost-admin/`.
 *   - auth rides the `roo_auth: Bearer <token>` header (the user API's header), not `roo_admin_auth`.
 *
 * ## Why the token arrives by injection instead of an import
 *
 * `session.service` needs the SDK (to call the auth endpoints) and the SDK needs a token (which
 * session owns) — a cycle. Rather than import each other, session REGISTERS its getters here at
 * startup. Same leaf-extraction pattern the mobile app uses for its paywall presenter and demo
 * block funnel, and it keeps this file dependency-free enough to unit test.
 */

type TokenProvider = () => Promise<string | null>;
type UnauthorizedHandler = () => void;

let getApiToken: TokenProvider = async () => null;
let onUnauthorized: UnauthorizedHandler = () => {};

/** Called once by session.service at startup. */
export function registerAuthHooks(p: { getApiToken: TokenProvider; onUnauthorized: UnauthorizedHandler }): void {
  getApiToken = p.getApiToken;
  onUnauthorized = p.onUnauthorized;
}

/**
 * Endpoints that take NO bearer token. All are `context.noAuth()` server-side.
 *
 * This is load-bearing, not an optimisation. The refresh endpoints are reached FROM the token
 * getter, so fetching a token for them re-enters `getApiToken` — and it re-enters before the
 * caller's in-flight guard has been assigned (an async IIFE only publishes its promise at its first
 * await, and the nested call happens in the synchronous prefix). The guard therefore can't see the
 * refresh it is itself performing, and each level starts another one. Measured before this list
 * existed: a single session restore fired `auth/refreshAccessToken` three times.
 *
 * Skipping auth here also stops a login attempt from trying to refresh a session that by definition
 * doesn't exist yet.
 */
const UNAUTHENTICATED_PATHS = [
  "auth/refreshAccessToken",
  "auth/refreshRefreshToken",
  "auth/webLogout",
  "user/loginStep1",
  "user/loginStep2",
  // Signup creates the session; there is nothing to authenticate with yet.
  "user/signUp",
  // Social sign-in, same reasoning as the two above: a provider ID token IS the credential, and a
  // signed-out user has no session to fetch a bearer token from. Omitting these is what re-enters
  // the token getter — the measured three-nested-refresh bug this list exists to prevent.
  "user/loginWithGoogle",
  "user/loginWithApple",
  "user/signUpWithGoogle",
  "user/signUpWithApple",
];

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly path: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** How long to wait before giving up on a request. */
const REQUEST_TIMEOUT_MS = 20_000;

const doFetch: DoFetch = async (p) => {
  const path = p.path.join("/");
  const url = `${getApiRootUrl()}/bifrost/${path}`;
  const isUnauthenticated = UNAUTHENTICATED_PATHS.includes(path);
  const token = isUnauthenticated ? null : await getApiToken();

  // No token for an authenticated path ⇒ no session. Fail here instead of sending the request.
  // Sending it anyway would (a) waste a guaranteed-401 round trip on every signed-out page load,
  // and worse (b) turn a TRANSIENT refresh failure into a forced logout: `getApiToken` returns null
  // on a network blip specifically to leave the session intact, but the resulting 401 would run
  // `onUnauthorized` and tear it down — the exact outcome that catch exists to avoid.
  if (!isUnauthenticated && !token) {
    throw new ApiError("No active session", null, path);
  }

  // Dates cross the wire as placeholders in both directions — the server decodes the same shape.
  // Skipping this would send raw ISO strings that arrive as strings, not Dates.
  const encodedPayload = sharedUniversal.encodeObjReplacingDatesWithPlaceholder(p.arg, {
    lid: "r-Ex6RN8eD",
    meta: { path },
  });

  try {
    const response = await axios.post(
      url,
      { payload: encodedPayload },
      {
        headers: token ? { roo_auth: `Bearer ${token}` } : {},
        // Load-bearing: the refresh token is an HttpOnly cookie, and a cross-ORIGIN request drops
        // cookies without this. `app.roo.money` and `api-latest.roo.money` are same-SITE (so
        // SameSite=Lax is satisfied) but different origins, so the browser still needs to be told
        // to attach credentials. The server's CORS allowlist echoes our exact origin and sets
        // Allow-Credentials for it; a wildcard origin would make the browser reject the response.
        withCredentials: true,
        // Axios defaults to 0 — wait forever. The whole app gates on `initSession`, so a host that
        // accepts the connection and never answers (a slot mid-rotation, a proxy blackhole, a
        // suspended laptop) would leave the user on "Loading…" permanently with no error and no
        // way forward but a manual reload.
        timeout: REQUEST_TIMEOUT_MS,
      },
    );
    return sharedUniversal.decodeObjReplacingDatePlaceholdersWithDates(response.data);
  } catch (e: any) {
    const status: number | null = e?.response?.status ?? null;
    // A 401 from an unauthenticated endpoint is a bad code or a spent refresh token — the session's
    // own repair path, which owns that outcome. Calling onUnauthorized here would clear tokens
    // mid-repair and turn a recoverable expiry into a forced re-login.
    if (status === 401 && !isUnauthenticated) {
      onUnauthorized();
    }
    throw new ApiError(e?.response?.data?.message ?? e?.message ?? "Request failed", status, path);
  }
};

export const { SDK, SDKProvider, getQueryKey, usePaginatedSDK, useSDK } = createTypedReactSDK<typeof apiTypes>({
  doFetch,
});
